import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Calendar, ArrowRight, ArrowLeft, Check, Swords } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = [
  { title: 'Credentials', subtitle: 'Email & password' },
  { title: 'Birthday', subtitle: 'Age check' },
  { title: 'Finish', subtitle: 'Create account' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dob, setDob] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  const clearErr = (field) => setErrors(prev => { const n = {...prev}; delete n[field]; return n; });

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!EMAIL_RE.test(email.trim())) e.email = 'Enter a valid email';
      if (!password || password.length < 8) e.password = 'Min 8 characters';
      if (confirmPassword !== password) e.confirmPassword = 'Passwords must match';
    }
    if (s === 1) {
      if (!dob) e.dob = 'Please pick your birthday';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step === 0) navigate('/login');
    else setStep(s => s - 1);
  };

  const handleSignup = async () => {
    const allValid = validateStep(0) && validateStep(1);
    if (!allValid) return;
    setServerError('');
    setLoading(true);
    try {
      const result = await register(email, password, dob);
      navigate('/confirm-email', { state: { email: email.trim(), code: result.confirmation_code } });
    } catch (err) {
      setServerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-root">
      <div className="auth-blobs">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-blob auth-blob-3" />
      </div>

      <div className="auth-card-wrap">
        {/* Back to home */}
        <Link to="/" className="auth-back-link">
          <ArrowLeft size={16} />
          <span>Home</span>
        </Link>

        {/* Header */}
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <img src="/rivalogo.png" alt="RivalCam" className="auth-logo" />
          </Link>
          <div className="signup-badge">RivalCAM</div>
          <h1 className="auth-title">Create your account</h1>
          <p className="auth-subtitle">Step {step + 1} of {STEPS.length} · {STEPS[step].title}</p>
        </div>

        {/* Card */}
        <div className="auth-card">
          <div className="auth-card-stripe" />

          {/* Stepper */}
          <div className="signup-stepper">
            {STEPS.map((meta, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <div key={meta.title} className={`signup-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                  onClick={() => { if (i < step) setStep(i); }}>
                  <div className="signup-step-circle">
                    {done ? <Check size={14} /> : <span>{i + 1}</span>}
                  </div>
                  <div className="signup-step-labels">
                    <span className="signup-step-title">{meta.title}</span>
                    <span className="signup-step-sub">{meta.subtitle}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="signup-step-content">
            {step === 0 && (
              <>
                <h2 className="auth-step-headline">Secure your login</h2>
                <p className="auth-step-desc">Use your best email and a strong password to access RivalCAM.</p>

                <div className="auth-field">
                  <label className="auth-label">Email</label>
                  <div className="auth-input-wrap" style={{ borderColor: errors.email ? '#F87171' : undefined }}>
                    <Mail size={18} className="auth-input-icon" color="#64748B" />
                    <input type="email" className="auth-input" placeholder="john@example.com"
                      value={email} onChange={e => { setEmail(e.target.value); clearErr('email'); }} />
                  </div>
                  {errors.email && <span className="auth-field-error">{errors.email}</span>}
                </div>

                <div className="auth-field">
                  <label className="auth-label">Password</label>
                  <div className="auth-input-wrap" style={{ borderColor: errors.password ? '#F87171' : undefined }}>
                    <Lock size={18} className="auth-input-icon" color="#64748B" />
                    <input type={showPass ? 'text' : 'password'} className="auth-input" placeholder="••••••••"
                      value={password} onChange={e => { setPassword(e.target.value); clearErr('password'); }} />
                    <button type="button" className="auth-eye" onClick={() => setShowPass(s => !s)}>
                      {showPass ? <EyeOff size={18} color="#64748B" /> : <Eye size={18} color="#64748B" />}
                    </button>
                  </div>
                  {errors.password && <span className="auth-field-error">{errors.password}</span>}
                  <span className="auth-helper">At least 8 characters with letters and numbers.</span>
                </div>

                <div className="auth-field">
                  <label className="auth-label">Confirm password</label>
                  <div className="auth-input-wrap" style={{ borderColor: errors.confirmPassword ? '#F87171' : undefined }}>
                    <Lock size={18} className="auth-input-icon" color="#64748B" />
                    <input type={showConfirm ? 'text' : 'password'} className="auth-input" placeholder="Repeat password"
                      value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); clearErr('confirmPassword'); }} />
                    <button type="button" className="auth-eye" onClick={() => setShowConfirm(s => !s)}>
                      {showConfirm ? <EyeOff size={18} color="#64748B" /> : <Eye size={18} color="#64748B" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="auth-field-error">{errors.confirmPassword}</span>}
                </div>

                <button className="auth-btn" onClick={handleNext}>
                  <span>Continue</span>
                  <ArrowRight size={18} />
                </button>
              </>
            )}

            {step === 1 && (
              <>
                <h2 className="auth-step-headline">Confirm your birthday</h2>
                <p className="auth-step-desc">We use your birth date to keep RivalCAM compliant with digital safety regulations.</p>

                <div className="auth-field">
                  <label className="auth-label">Date of birth</label>
                  <div className="auth-input-wrap" style={{ borderColor: errors.dob ? '#F87171' : undefined }}>
                    <Calendar size={18} className="auth-input-icon" color="#64748B" />
                    <input type="date" className="auth-input auth-input-date"
                      value={dob} max={new Date().toISOString().split('T')[0]}
                      onChange={e => { setDob(e.target.value); clearErr('dob'); }} />
                  </div>
                  {errors.dob && <span className="auth-field-error">{errors.dob}</span>}
                  <div className="auth-notice">
                    <span className="auth-notice-icon">⚖️</span>
                    <span>We collect your birthday to comply with digital safety laws.</span>
                  </div>
                </div>

                <button className="auth-btn" onClick={handleNext}>
                  <span>Continue</span>
                  <ArrowRight size={18} />
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="auth-step-headline">Almost there</h2>
                <p className="auth-step-desc">Review your info and create your RivalCAM account.</p>

                <div className="signup-summary">
                  <div className="signup-summary-row">
                    <div className="signup-summary-icon"><Mail size={18} color="#38BDF8" /></div>
                    <div>
                      <div className="signup-summary-label">Email</div>
                      <div className="signup-summary-value">{email}</div>
                    </div>
                  </div>
                  <div className="signup-summary-row">
                    <div className="signup-summary-icon"><Calendar size={18} color="#38BDF8" /></div>
                    <div>
                      <div className="signup-summary-label">Birthday</div>
                      <div className="signup-summary-value">{dob || '—'}</div>
                    </div>
                  </div>
                </div>

                <p className="auth-policy-note" style={{ marginTop: '12px' }}>
                  By creating an account you acknowledge our{' '}
                  <a href="https://meanc.net/info/terms" target="_blank" rel="noreferrer" className="auth-policy-link">Terms</a>
                  {' '}and{' '}
                  <a href="https://meanc.net/info/community-guidelines" target="_blank" rel="noreferrer" className="auth-policy-link">Community Guidelines</a>.
                </p>

                {serverError && <div className="auth-server-error">{serverError}</div>}

                <button className="auth-btn" onClick={handleSignup} disabled={loading}>
                  {loading ? <span className="auth-spinner" /> : <span>Create account</span>}
                </button>
              </>
            )}

            {/* Back button */}
            <button className="auth-back-btn" onClick={handleBack}>
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>
          </div>
        </div>

        <p className="auth-footer-text">
          Already have an account?{' '}
          <Link to="/login" className="auth-footer-link">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
